import React, { useEffect } from 'react';
import {
  Badge,
  Box,
  Button,
  Container,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Grid,
  GridItem,
  IconButton,
  HStack,
  Image,
  Link,
  StackDivider,
  Stack,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { HamburgerIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { apiLogout, getStoredAuth, isAdminUser } from '../api/client';

interface PortalPageShellProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
}

export default function PortalPageShell({
  title: _title,
  subtitle,
  children,
  actions,
}: PortalPageShellProps) {
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { user } = getStoredAuth();
  const isAdmin = isAdminUser(user);
  const displayName = user?.full_name?.trim() || user?.email || '';

  useEffect(() => {
    const redirectToLoginIfExpired = () => {
      const { token } = getStoredAuth();

      if (!token) {
        onClose();
        navigate('/member/login', { replace: true });
      }
    };

    redirectToLoginIfExpired();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        redirectToLoginIfExpired();
      }
    };

    window.addEventListener('focus', redirectToLoginIfExpired);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const intervalId = window.setInterval(redirectToLoginIfExpired, 30000);

    return () => {
      window.removeEventListener('focus', redirectToLoginIfExpired);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [navigate, onClose]);

  const handleLogout = async () => {
    await apiLogout();
    onClose();
    navigate('/member/login', { replace: true });
  };

  const navLinks = (
    <>
      {isAdmin ? (
        <>
          <Link as={RouterLink} to="/admin" color="gray.700" fontWeight="600">
            Dashboard
          </Link>
          <Link as={RouterLink} to="/admin/plans" color="gray.700" fontWeight="600">
            Subscriptions
          </Link>
          <Link as={RouterLink} to="/admin/subscribers" color="gray.700" fontWeight="600">
            Subscribers
          </Link>
          <Link as={RouterLink} to="/admin/articles" color="gray.700" fontWeight="600">
            Manage Articles
          </Link>
          <Link as={RouterLink} to="/admin/articles/new" color="gray.700" fontWeight="600">
            Create Article
          </Link>
        </>
      ) : (
        <>
          <Link as={RouterLink} to="/member/articles" color="gray.700" fontWeight="600">
            Articles
          </Link>
          <Link as={RouterLink} to="/member/subscription" color="gray.700" fontWeight="600">
            Subscription
          </Link>
        </>
      )}
    </>
  );

  return (
    <Box minH="100vh" bg="gray.50">
      <Box bg="white" borderBottomWidth="1px" borderColor="gray.200">
        <Container maxW="7xl" py={4}>
          <Grid
            templateColumns={{ base: '1fr auto', lg: 'auto 1fr auto' }}
            alignItems="center"
            gap={{ base: 4, lg: 8 }}
          >
            <GridItem>
              <Stack spacing={2}>
                <Link as={RouterLink} to="/" width="fit-content">
                  <Image
                    src="/logo/logo_black.png"
                    alt="Mercurian Equity"
                    height={{ base: '28px', sm: '34px', md: '44px', lg: '48px' }}
                    width="auto"
                    maxW={{ base: '145px', sm: '170px', md: '220px', lg: '240px' }}
                    objectFit="contain"
                  />
                </Link>
              </Stack>
            </GridItem>

            <GridItem display={{ base: 'none', lg: 'block' }} justifySelf="center">
              <HStack spacing={8} justify="center">
                {navLinks}
              </HStack>
            </GridItem>

            <GridItem justifySelf="end">
              <HStack spacing={3} display={{ base: 'none', lg: 'flex' }}>
                {user?.role ? <Badge colorScheme={user.role === 'admin' ? 'purple' : 'green'}>{user.role}</Badge> : null}
                {displayName ? <Text color="gray.600" fontSize="sm">{displayName}</Text> : null}
                <Button size="sm" colorScheme="red" variant="outline" onClick={handleLogout}>Logout</Button>
              </HStack>

              <IconButton
                aria-label="Open navigation menu"
                icon={<HamburgerIcon />}
                variant="outline"
                onClick={onOpen}
                display={{ base: 'inline-flex', lg: 'none' }}
              />
            </GridItem>
          </Grid>
        </Container>
      </Box>

      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Image
              src="/logo/logo_black.png"
              alt="Mercurian Equity"
              height={{ base: '28px', sm: '34px' }}
              width="auto"
              maxW={{ base: '145px', sm: '170px' }}
              objectFit="contain"
            />
          </DrawerHeader>

          <DrawerBody py={6}>
            <VStack align="stretch" spacing={5} divider={<StackDivider borderColor="gray.200" />}>
              <Stack spacing={4}>
                {subtitle ? <Text color="gray.600">{subtitle}</Text> : null}
                <VStack align="stretch" spacing={4}>
                  {isAdmin ? (
                    <>
                      <Link as={RouterLink} to="/admin" onClick={onClose} fontWeight="600">Dashboard</Link>
                      <Link as={RouterLink} to="/admin/plans" onClick={onClose} fontWeight="600">Subscriptions</Link>
                      <Link as={RouterLink} to="/admin/subscribers" onClick={onClose} fontWeight="600">Subscribers</Link>
                      <Link as={RouterLink} to="/admin/articles" onClick={onClose} fontWeight="600">Manage Articles</Link>
                      <Link as={RouterLink} to="/admin/articles/new" onClick={onClose} fontWeight="600">Create Article</Link>
                    </>
                  ) : (
                    <>
                      <Link as={RouterLink} to="/member/articles" onClick={onClose} fontWeight="600">Articles</Link>
                      <Link as={RouterLink} to="/member/subscription" onClick={onClose} fontWeight="600">Subscription</Link>
                    </>
                  )}
                </VStack>
              </Stack>

              <Stack spacing={3}>
                {user?.role ? <Badge width="fit-content" colorScheme={user.role === 'admin' ? 'purple' : 'green'}>{user.role}</Badge> : null}
                {displayName ? <Text color="gray.600" fontSize="sm">{displayName}</Text> : null}
                <Button size="sm" colorScheme="red" variant="outline" onClick={handleLogout} width="fit-content">
                  Logout
                </Button>
              </Stack>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Container maxW="7xl" py={{ base: 6, md: 8 }}>
        <Stack spacing={6}>
          {actions ? <Flex justify="flex-end">{actions}</Flex> : null}
          <Box
            bg="white"
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="2xl"
            p={{ base: 5, md: 8 }}
            boxShadow="0 12px 40px rgba(15, 23, 42, 0.06)"
          >
            {children}
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
